import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import Map, { Marker, Popup } from 'react-map-gl';
import useSupercluster from 'use-supercluster';
import 'mapbox-gl/dist/mapbox-gl.css';
import { db } from '../firebaseConfig';
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
  arrayUnion
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { calculateDistance } from '../utils/geo';
import ChatModal from '../ChatModal';
import { toast } from 'react-toastify';
import { sendNotification } from '../utils/notifications';
import { getNextClubDate } from '../utils/schedule';

import UserProfile from './UserProfile';

// Ensure token is available
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const getSeenMsgIds = (sessionId, userId) => {
  const key = `seenMsgs-${sessionId}-${userId}`;
  return new Set(JSON.parse(localStorage.getItem(key) || '[]'));
};

const markMsgAsSeen = (sessionId, userId, msgId) => {
  const key = `seenMsgs-${sessionId}-${userId}`;
  const current = getSeenMsgIds(sessionId, userId);
  current.add(msgId);
  const limited = Array.from(current).slice(-50);
  localStorage.setItem(key, JSON.stringify(limited));
  return new Set(limited);
};

const MapView = forwardRef(({ onOpenChat, activeFilter, onTriggerLogin, onOpenCalendar, onEditSession, userLocation, onViewProfile, searchRadius }, ref) => {
  const { user } = useAuth();
  const [viewState, setViewState] = useState({
    latitude: 33.7701,
    longitude: -118.1937,
    zoom: 9
  });
  const [sessions, setSessions] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [chatSession, setChatSession] = useState(null);
  const [viewProfileId, setViewProfileId] = useState(null);
  const prevParticipantsMap = useRef({});
  const mapRef = useRef();
  const [bounds, setBounds] = useState(null);

  useImperativeHandle(ref, () => ({
    flyToLocation: (session) => {
      let lat, lng;
      if (session.location && typeof session.location === 'string') {
        [lat, lng] = session.location.split(',').map(Number);
      } else if (session.location?.latitude) {
        lat = session.location.latitude;
        lng = session.location.longitude;
      }
      
      if (lat && lng && !isNaN(lat)) {
        setViewState(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          zoom: 14
        }));
        setSelected(session);
      }
    }
  }));

  // ... (useEffects remain same)

  // FILTERING LOGIC
  const visibleSessions = sessions.filter(session => {
    if (activeFilter === 'clubs') return false;
    if (activeFilter === 'all') return true;
    if (activeFilter === 'now') return session.type !== 'scheduled';
    if (activeFilter === 'scheduled') return session.type === 'scheduled';
    return true;
  });

  const visibleClubs = (activeFilter === 'all' || activeFilter === 'clubs') ? clubs : [];

  // Helper to handle join safely
  const handleJoinGame = async (session) => {
    if (!user) {
      if (onTriggerLogin) onTriggerLogin();
      else toast.error("Please login to join!");
      return;
    }
    try {
      await updateDoc(doc(db, 'sessions', session.id), {
        participants: arrayUnion(user.uid),
      });
      setSelected({
        ...session,
        participants: [...(session.participants || []), user.uid],
      });
      toast.success("Joined game!");
    } catch (err) {
      console.error("Error joining:", err);
      toast.error("Failed to join game");
    }
  };

  // Center on user location once available
  const hasCenteredRef = useRef(false);
  useEffect(() => {
    if (userLocation && !hasCenteredRef.current) {
      // Only center if we haven't already (and maybe arguably if we aren't deep linked?)
      // The deep link logic runs below and might override this, which is fine (deep link should win).
      // But to be safe, let's just update.
      setViewState(prev => ({
        ...prev,
        latitude: userLocation.lat,
        longitude: userLocation.lng,
        zoom: 11,
      }));
      hasCenteredRef.current = true;
    }
  }, [userLocation]);

  useEffect(() => {
    if (!user) return;
    const unsubscribes = [];

    const unsubSessions = onSnapshot(collection(db, 'sessions'), snapshot => {
      snapshot.docChanges().forEach(change => {
        const session = { id: change.doc.id, ...change.doc.data() };
        const prevParticipants = prevParticipantsMap.current[session.id] || [];
        const currParticipants = session.participants || [];

        const joined = currParticipants.filter(uid => !prevParticipants.includes(uid));
        joined.forEach(uid => {
          if (user && uid !== user.uid) {
            toast.info(`🧍 Someone joined ${session.name}`);
            if (document.visibilityState === 'hidden') {
              sendNotification(`Someone joined ${session.name}`, {
                body: 'Click to view the map',
                tag: `join-${session.id}`
              });
            }
          }
        });
        prevParticipantsMap.current[session.id] = currParticipants;

        const isHost = user && session.uid === user.uid;
        const isParticipant = user && currParticipants.includes(user.uid);
        if (isHost || isParticipant) {
          const msgsRef = collection(db, `sessions/${session.id}/messages`);
          const unsubMsgs = onSnapshot(msgsRef, msgSnap => {
            let seenIds = getSeenMsgIds(session.id, user ? user.uid : 'guest');
            msgSnap.docChanges().forEach(change => {
              if (change.type === 'added') {
                const msg = change.doc.data();
                const msgId = change.doc.id;
                if (user && msg.uid !== user.uid && !seenIds.has(msgId)) {
                  toast.info(`💬 ${msg.displayName || 'User'}: ${msg.text.slice(0, 50)}...`, {
                    onClick: () => {
                      setSelected(session);
                      setViewState(prev => ({
                        ...prev,
                        latitude: parseFloat(session.location.split(',')[0]),
                        longitude: parseFloat(session.location.split(',')[1]),
                        zoom: 13
                      }));
                    }
                  });

                  if (document.visibilityState === 'hidden') {
                    sendNotification(`New message in ${session.name}`, {
                      body: `${msg.displayName || 'Someone'}: ${msg.text}`,
                      tag: `msg-${session.id}`,
                      onClick: () => {
                        window.focus();
                        setSelected(session);
                        setChatSession(session);
                      }
                    });
                  }

                  seenIds = markMsgAsSeen(session.id, user ? user.uid : 'guest', msgId);
                }
              }
            });
          });
          unsubscribes.push(unsubMsgs);
        }
      });
    });

    unsubscribes.push(unsubSessions);
    return () => unsubscribes.forEach(fn => fn());
  }, [user]);

  // Deep Linking Logic
  const hasDeepLinked = useRef(false);
  useEffect(() => {
    if (hasDeepLinked.current) return;
    if (sessions.length === 0 && clubs.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const deepLinkId = params.get('id');

    if (deepLinkId) {
      const foundSession = sessions.find(s => s.id === deepLinkId);
      const foundClub = clubs.find(c => c.id === deepLinkId);
      const target = foundSession || foundClub;

      if (target) {
        setSelected(target);

        // Parse location
        let lat, lng;
        if (target.location && typeof target.location === 'string') {
          [lat, lng] = target.location.split(',').map(Number);
        } else if (target.location?.latitude) {
          lat = target.location.latitude;
          lng = target.location.longitude;
        }

        if (lat && lng && !isNaN(lat)) {
          setViewState(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            zoom: 14
          }));
        }
        hasDeepLinked.current = true;
      }
    }
  }, [sessions, clubs]);



  useEffect(() => {
    // 1. Sessions
    const unsubSessions = onSnapshot(collection(db, 'sessions'), (snapshot) => {
      const now = new Date();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(s => {
        // "Available Now" sessions expire at availableUntil
        if (s.type === 'now' && s.availableUntil) {
          return s.availableUntil.toDate?.() > now;
        }

        // "Scheduled" sessions expire 3 hours after start time
        if (s.type === 'scheduled' && s.scheduledStartTime) {
          const startTime = s.scheduledStartTime.toDate?.() || new Date(s.scheduledStartTime);
          const endTime = new Date(startTime.getTime() + 3 * 60 * 60 * 1000); // +3 hours
          return now < endTime;
        }

        // Fallback for old/bad data: expire 4 hours after creation
        if (s.createdAt) {
          return (now - s.createdAt.toDate?.()) / 36e5 <= 4;
        }
        return false;
      });
      setSessions(data);
    });

    // 2. Clubs
    const unsubClubs = onSnapshot(collection(db, 'clubs'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const visibleClubs = data.filter(club => {
        if (club.status === 'approved') return true;
        if (user && (user.uid === club.ownerId || user.isAdmin)) return true;
        return false;
      });
      setClubs(visibleClubs);
    });

    return () => {
      unsubSessions();
      unsubClubs();
    };
  }, []);

  function timeAgo(date) {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return hours < 1 ? (minutes < 1 ? 'just now' : `${minutes}m ago`) : (`${hours}h ago`);
  }

  // Prepare points for use-supercluster
  const points = visibleSessions.filter(session => {
    if (!userLocation || !searchRadius || searchRadius >= 10000) return true;
    const [lat, lng] = (session.location || '').split(',').map(Number);
    if (isNaN(lat) || isNaN(lng)) return false;
    const dist = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
    return dist <= searchRadius;
  }).map(session => {
    const [lat, lng] = (session.location || '').split(',').map(Number);
    if (isNaN(lat) || isNaN(lng)) return null;
    return {
      type: 'Feature',
      properties: { cluster: false, sessionId: session.id, ...session },
      geometry: { type: 'Point', coordinates: [lng, lat] }
    };
  }).filter(Boolean);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: viewState.zoom,
    options: { radius: 75, maxZoom: 20 }
  });

  return (
    <div style={{ width: '100%', height: '80vh', position: 'relative' }}>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => {
          setViewState(evt.viewState);
          if (mapRef.current) setBounds(mapRef.current.getMap().getBounds().toArray().flat());
        }}
        onLoad={() => {
          if (mapRef.current) setBounds(mapRef.current.getMap().getBounds().toArray().flat());
        }}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
      >
        {/* Calendar Button Overlay */}
        <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
          <button
            onClick={onOpenCalendar}
            className="bg-white hover:bg-gray-50 text-indigo-600 font-semibold py-2 px-3 rounded-lg shadow-md border border-gray-200 flex items-center gap-2 transition-all hover:scale-105"
            title="Open Calendar"
          >
            <span className="text-xl">📅</span>
            <span className="hidden sm:inline">Calendar</span>
          </button>
        </div>
        {clusters.map(cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster, point_count: pointCount } = cluster.properties;

          if (isCluster) {
            return (
              <Marker key={`cluster-${cluster.id}`} latitude={latitude} longitude={longitude}>
                <div
                  style={{
                    width: `${Math.min(60, 40 + (pointCount / points.length) * 20)}px`,
                    height: `${Math.min(60, 40 + (pointCount / points.length) * 20)}px`,
                    borderRadius: '50%',
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.4)',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    border: '3px solid white',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onClick={() => {
                    const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(cluster.id), 20);
                    setViewState({ ...viewState, latitude, longitude, zoom: expansionZoom, transitionDuration: 300 });
                  }}
                >
                  {pointCount}
                </div>
              </Marker>
            );
          }

          const player = cluster.properties;
          return (
            <Marker key={player.sessionId} latitude={latitude} longitude={longitude}>
              <div
                onClick={() => setSelected(player)}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: player.type === 'scheduled' ? '#3b82f6' : '#22c55e', // Blue vs Green
                  boxShadow: '0 0 5px rgba(0,0,0,0.5)',
                  cursor: 'pointer',
                  border: '2px solid white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px',
                  position: 'relative'
                }}
                title={player.name}
              >
                ♟️
                {(player.participants?.length > 0) && (
                  <div style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    border: '1px solid white'
                  }}>
                    {1 + player.participants.length}
                  </div>
                )}
              </div>
            </Marker>
          );
        })}

        {visibleClubs.filter(club => {
          if (!userLocation || !searchRadius || searchRadius >= 10000) return true;
          let lat, lng;
          if (club.location && typeof club.location === 'string') {
            [lat, lng] = club.location.split(',').map(Number);
          } else if (club.location && club.location.latitude) {
            lat = club.location.latitude;
            lng = club.location.longitude;
          }
          if (!lat || !lng || isNaN(lat)) return false;
          return calculateDistance(userLocation.lat, userLocation.lng, lat, lng) <= searchRadius;
        }).map(club => {
          // Handle legacy 'lat,lng' string or GeoPoint objects if that changes, but for now assuming string from AddressInput
          let lat, lng;
          if (club.location && typeof club.location === 'string') {
            [lat, lng] = club.location.split(',').map(Number);
          } else if (club.location && club.location.latitude) {
            lat = club.location.latitude;
            lng = club.location.longitude;
          }

          if (!lat || !lng || isNaN(lat)) return null;

          return (
            <Marker key={club.id} latitude={lat} longitude={lng}>
              <div
                onClick={() => setSelected(club)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#f97316', // Orange
                  boxShadow: '0 0 8px rgba(249, 115, 22, 0.6)',
                  cursor: 'pointer',
                  border: '2px solid white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px',
                  zIndex: 10
                }}
                title={club.name}
              >
                🏰
              </div>
            </Marker>
          );
        })}

        {chatSession && <ChatModal session={chatSession} onClose={() => setChatSession(null)} />}

        {selected && (() => {
          let isTooFar = false;
          let distance = null;
          if (userLocation && selected.location) {
            const [latStr, lngStr] = (selected.location.coordinates || selected.location).split?.(',') || [selected.location.latitude, selected.location.longitude];
            if (latStr && lngStr) {
              distance = calculateDistance(userLocation.lat, userLocation.lng, parseFloat(latStr), parseFloat(lngStr));
              if (distance > (searchRadius && searchRadius < 10000 ? searchRadius : 50)) isTooFar = true;
            }
          }

          return (
            <Popup
              latitude={parseFloat((selected.location.split?.(',') || [])[0] || selected.location.latitude)}
              longitude={parseFloat((selected.location.split?.(',') || [])[1] || selected.location.longitude)}
              onClose={() => setSelected(null)}
              closeOnClick={false}
              closeButton={false} // Disable default small button
              maxWidth="300px"
              className="custom-popup"
            >
              <div className="p-2 min-w-[200px] relative" style={{ padding: '10px' }}>
                {/* Custom Close Button */}
                <button
                  onClick={() => setSelected(null)}
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 20,
                    color: '#666'
                  }}
                  aria-label="Close popup"
                >
                  ✕
                </button>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', paddingRight: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {/* Avatar */}
                    {selected.photoURL ? (
                      <img
                        src={selected.photoURL}
                        alt="Host"
                        className="w-8 h-8 rounded-full border border-gray-200 object-cover cursor-pointer hover:opacity-80"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewProfile) onViewProfile(selected.uid);
                        }}
                      />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center border border-indigo-200 text-indigo-700 font-bold text-xs cursor-pointer hover:opacity-80"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewProfile) onViewProfile(selected.uid);
                        }}
                      >
                        {(selected.displayName || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Host</p>
                      <p
                        className="text-sm font-bold text-gray-800 leading-none cursor-pointer hover:text-indigo-600 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewProfile) onViewProfile(selected.uid);
                        }}
                      >
                        {selected.displayName}
                      </p>
                    </div>
                  </div>
                  {!isTooFar && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(`${window.location.origin}/?id=${selected.id}`)
                          .then(() => toast.success("Link copied!"))
                          .catch(() => toast.error("Failed to copy path"));
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}
                      title="Copy Link"
                    >
                      🔗
                    </button>
                  )}
                </div>
                <p style={{ fontSize: '0.9em', color: '#666' }}>
                  {isTooFar ? <span className="text-gray-400 italic">Location hidden (too far)</span> : selected.address}
                </p>

                {!isTooFar && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selected.address || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'block', margin: '5px 0', fontSize: '0.8em', color: '#007bff' }}
                  >
                    📍 Open in Google Maps
                  </a>
                )}

                {selected.note && (
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 mt-2 mb-2 italic border border-gray-100">
                    "{selected.note}"
                  </div>
                )}

                {selected.type !== 'club' && (
                  <div style={{ margin: '8px 0', fontSize: '0.85em', color: '#555' }}>
                    {selected.isKidFriendly && (
                      <div style={{ display: 'inline-block', backgroundColor: '#ecfdf5', color: '#047857', padding: '2px 6px', borderRadius: '4px', marginRight: '5px', fontWeight: 'bold' }}>
                        ✓ Kid Friendly
                      </div>
                    )}
                    {selected.isBeginnerFriendly && (
                      <div style={{ display: 'inline-block', backgroundColor: '#f0fdfa', color: '#0d9488', padding: '2px 6px', borderRadius: '4px', marginRight: '5px', fontWeight: 'bold' }}>
                        🌱 Beginner Friendly
                      </div>
                    )}
                    {selected.availableUntil && (
                      <div style={{ marginTop: '4px', color: '#666' }}>
                        ⏱️ Available until {new Date(selected.availableUntil.toDate ? selected.availableUntil.toDate() : selected.availableUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                    {selected.type === 'scheduled' && selected.scheduledStartTime && (
                      <div style={{ marginTop: '4px', color: '#666' }}>
                        🗓️ Starts {new Date(selected.scheduledStartTime.toDate ? selected.scheduledStartTime.toDate() : selected.scheduledStartTime).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}

                    {/* Participants List */}
                    {selected.participants && selected.participants.length > 0 && (
                      <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                        <p style={{ fontSize: '0.8em', color: '#888', marginBottom: '4px' }}>Players ({selected.participants.length})</p>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {/* Placeholder for future participant avatars */}
                        </div>
                      </div>
                    )}

                    <div className="mt-2 text-sm text-blue-600 font-semibold bg-blue-50 p-1.5 rounded text-center">
                      👥 {(selected.participants || []).length} Players Joined
                    </div>
                  </div>
                )}

                {selected.type === 'club' && (
                  <div style={{ fontSize: '0.8em', color: '#f97316', fontWeight: 'bold', marginBottom: '5px' }}>
                    🏰 CLUB
                    {selected.schedule ? (
                      <div style={{ marginTop: '4px', fontWeight: 'normal', color: '#333' }}>
                        {selected.schedule.map((slot, idx) => {
                          const startTime = new Date(`1970-01-01T${slot.time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                          const endTime = slot.endTime ? new Date(`1970-01-01T${slot.endTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
                          return (
                            <div key={idx}>
                              🗓️ {slot.day}s @ {startTime}{endTime ? ` - ${endTime}` : ''}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ marginTop: '4px', fontWeight: 'normal', color: '#333' }}>
                        🗓️ {selected.meetingDay}s @ {new Date(`1970-01-01T${selected.meetingTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                )}

                {user?.uid !== selected.uid && selected.type !== 'club' && !(selected.participants || []).includes(user?.uid) && (
                  <button
                    disabled={isTooFar}
                    title={isTooFar ? "Must be within 50 miles to join" : ""}
                    onClick={() => handleJoinGame(selected)}
                    style={{
                      marginTop: '5px',
                      padding: '5px',
                      width: '100%',
                      background: isTooFar ? '#ccc' : '#2ecc71',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isTooFar ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isTooFar ? 'Too Far to Join' : 'Join Game'}
                  </button>
                )}

                <div className="flex gap-2 mt-3">
                  {/* Chat Button */}
                  <button
                    disabled={isTooFar}
                    title={isTooFar ? "Too far to chat" : ""}
                    onClick={() => {
                      if (!user) {
                        if (onTriggerLogin) onTriggerLogin();
                        else toast.error("Please login to chat!");
                      } else if (selected.type === 'club') {
                        const nextDate = getNextClubDate(selected);
                        if (nextDate) {
                          setChatSession({
                            ...selected,
                            id: `${selected.id}_${nextDate.toISOString()}`,
                            date: nextDate,
                            name: selected.name
                          });
                        } else {
                          setChatSession(selected);
                        }
                      } else {
                        setChatSession(selected);
                      }
                    }}
                    className={`flex-1 py-2 px-1 rounded shadow-sm text-xs font-bold transition-all flex flex-col items-center justify-center gap-1 ${isTooFar
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                  >
                    <span>💬</span>
                    <span>Chat</span>
                  </button>

                  {/* Host Actions: Edit & Delete */}
                  {user?.uid === selected.uid && selected.type !== 'club' && (
                    <>
                      <button
                        onClick={() => onEditSession(selected)}
                        className="flex-1 py-2 px-1 bg-white border border-gray-200 text-gray-700 rounded shadow-sm text-xs font-bold hover:bg-gray-50 transition-all flex flex-col items-center justify-center gap-1"
                        title="Edit Session"
                      >
                        <span>✏️</span>
                        <span>Edit</span>
                      </button>

                      <button
                        onClick={async () => {
                          if (window.confirm('Delete this session?')) {
                            try {
                              await deleteDoc(doc(db, 'sessions', selected.id));
                              toast.success('Session deleted');
                              setSelected(null);
                            } catch (err) {
                              console.error(err);
                              toast.error('Failed to delete');
                            }
                          }
                        }}
                        className="flex-1 py-2 px-1 bg-white border border-red-200 text-red-600 rounded shadow-sm text-xs font-bold hover:bg-red-50 transition-all flex flex-col items-center justify-center gap-1"
                        title="Delete Session"
                      >
                        <span>🗑️</span>
                        <span>Delete</span>
                      </button>
                    </>
                  )}

                  {/* Club Action: View Details (if reusing same space) or just keep chat big */}
                </div>

                {selected.type === 'club' && (
                  <>
                    <button
                      onClick={() => {
                        const el = document.getElementById(`club-${selected.id}`);
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          el.classList.add('ring-2', 'ring-indigo-600');
                          setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-600'), 2000);
                        } else {
                          // Fallback if not found (maybe tab inactive)
                          const listContainer = document.getElementById('session-list');
                          if (listContainer) listContainer.scrollIntoView({ behavior: 'smooth' });
                          toast.info("See 'Clubs' tab for details");
                        }
                        setSelected(null);
                      }}
                      style={{
                        marginTop: '5px',
                        padding: '5px',
                        width: '100%',
                        background: '#f3f4f6',
                        color: '#4b5563',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em'
                      }}
                    >
                      ℹ️ More Info
                    </button>
                  </>
                )}
              </div>
            </Popup>
          );
        })()}

        {viewProfileId && (
          <UserProfile userId={viewProfileId} onClose={() => setViewProfileId(null)} />
        )}
      </Map>
    </div >
  );
});

export default MapView;
